import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 120).bind(this);
  #isUpdatingQuantity = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * Only the cart component that owns the quantity selector should process the event.
   * This prevents cart drawer and cart page components from sending duplicate /cart/change requests.
   *
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (this.#isUpdatingQuantity) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const row = this.#getRowFromEvent(event) || this.#getRowByLine(line);
    const key = row?.dataset.key || null;
    const normalizedQuantity = Math.max(0, Number(quantity) || 0);

    if (!key && !line) return;

    this.updateQuantity({
      line,
      key,
      quantity: normalizedQuantity,
      action: normalizedQuantity === 0 ? 'clear' : 'change',
    });
  }

  /**
   * Handles the line item removal.
   * Shopify's response is the only source of truth.
   * Rows are not removed optimistically, because on mobile the drawer can otherwise keep stale DOM.
   *
   * @param {number | Event} lineOrEvent - The line item index or event.
   * @param {Event} [event] - The click event when called through declarative handlers.
   */
  onLineItemRemove(lineOrEvent, event) {
    if (this.#isUpdatingQuantity) return;

    const line = typeof lineOrEvent === 'number' ? lineOrEvent : undefined;
    const sourceEvent = event || (lineOrEvent instanceof Event ? lineOrEvent : undefined);
    const row = this.#getRowFromEvent(sourceEvent) || this.#getRowByLine(line);
    const key = row?.dataset.key || null;

    if (!key && !line) return;

    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates a cart line.
   * Uses the stable Shopify line item key when available. Line index is fallback only.
   *
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line index fallback.
   * @param {string | null} [config.key] - The stable Shopify line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.#isUpdatingQuantity) return;
    this.#performQuantityUpdate(config);
  }

  /**
   * Performs the cart quantity update.
   *
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line index fallback.
   * @param {string | null} [config.key] - The stable Shopify line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  #performQuantityUpdate(config) {
    const { line, key, quantity } = config;

    if (!key && !line) return;

    this.#isUpdatingQuantity = true;
    this.setAttribute('aria-busy', 'true');
    this.#disableCartItems();

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const changePayload = {
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (key) {
      changePayload.id = key;
    } else {
      changePayload.line = line;
    }

    cartTotal?.shimmer();
    this.#shimmerLine(line);

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: JSON.stringify(changePayload) }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];

        if (!sectionHTML) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, sectionHTML);
      })
      .catch((error) => {
        console.error(error);
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .finally(() => {
        this.#isUpdatingQuantity = false;
        this.removeAttribute('aria-busy');
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Finds the cart row that owns an event target.
   * @param {Event | undefined} event - The event.
   * @returns {HTMLTableRowElement | null}
   */
  #getRowFromEvent(event) {
    if (!event || !(event.target instanceof Element)) return null;

    const row = event.target.closest('.cart-items__table-row');

    if (!(row instanceof HTMLTableRowElement) || !this.contains(row)) return null;

    return row;
  }

  /**
   * Finds a cart row by line index fallback.
   * @param {number | undefined} line - The cart line index.
   * @returns {HTMLTableRowElement | null}
   */
  #getRowByLine(line) {
    if (!line) return null;

    return this.refs.cartItemRows[line - 1] || null;
  }

  /**
   * Adds shimmer only to the affected line and cart total.
   * @param {number | undefined} line - The line item index.
   */
  #shimmerLine(line) {
    const row = this.#getRowByLine(line);

    if (!row) return;

    const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number | undefined} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = line ? this.refs.quantitySelectors[line - 1] : null;
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Temporarily blocks cart row controls while Shopify confirms the mutation.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Re-enables cart row controls.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}